import React from "react";
import {useHistory, Link} from "react-router-dom";
import {
    makeStyles,
    Typography,
    IconButton,
    Button,
    Box
} from "@material-ui/core";
import {Add, DragHandle as DragHandleIcon} from "@material-ui/icons";
import {DragDropContext, Droppable, Draggable} from "react-beautiful-dnd";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import {Edit, Delete} from "@material-ui/icons";
import apiHelper from "../../../util/ApiHelper";
import {useLoading} from "../../../util/LoadingProvider";

const useStyles = makeStyles((theme) => ({
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    text: {
        textDecoration: "none",
        color: "#306F9D",
    },
}));

const PlanFeatureListingPage = (props) => {
    const {match, disabled} = props;
    const classes = useStyles();
    const history = useHistory();
    const [data, setData] = React.useState([]);
    const [selectedTab, setSelectedTab] = React.useState(0);
    const [allData, setAllData] = React.useState([]);

    const {setLoading} = useLoading();

    const getPlanFeatures = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.getPlanFeature();
            console.log('PLAN FEATURE RESULT:', result);
            
            if (result && result.data) {
                setAllData(result.data);
                // Default to All tab (tab 0)
                setData(result.data);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [setLoading]);

    React.useEffect(() => {
        getPlanFeatures();
    }, [getPlanFeatures]);

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        applyFilter(newValue, allData);
    };

    const applyFilter = React.useCallback((tabIndex, dataToFilter) => {
        if (tabIndex === 0) {
            // Show all
            setData(dataToFilter);
        } else if (tabIndex === 1) {
            // Show homepage
            setData(dataToFilter.filter(item => item.type === 'homepage'));
        } else if (tabIndex === 2) {
            // Show register
            setData(dataToFilter.filter(item => item.type === 'register'));
        } else if (tabIndex === 3) {
            // Show renewal
            setData(dataToFilter.filter(item => item.type === 'renewal'));
        }
    }, []);

    // Reapply filter when allData changes
    React.useEffect(() => {
        if (allData.length > 0) {
            applyFilter(selectedTab, allData);
        }
    }, [allData, selectedTab, applyFilter]);

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const onDragEnd = React.useCallback(async (result) => {
        if (!result.destination) {
            return;
        }

        if (result.destination.index === result.source.index) {
            return;
        }

        const reorderedData = reorder(data, result.source.index, result.destination.index);
        setData(reorderedData);

        // Update sequences based on new order
        try {
            setLoading(true);
            const seqData = {};
            reorderedData.forEach((item, index) => {
                seqData[item.id] = index + 1;
            });

            const response = await apiHelper.hkiff.bulkUpdatePlanFeatureSequence(seqData);

            if (response && (response.status === 200 || response.data)) {
                // Refresh the list to get updated data
                await getPlanFeatures();
            } else {
                // Revert on error
                applyFilter(selectedTab, allData);
                alert("Failed to update sequence. Please try again.");
            }
        } catch (error) {
            console.error("Error updating sequence:", error);
            // Revert on error
            applyFilter(selectedTab, allData);
            alert("An error occurred while updating sequence. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [data, allData, selectedTab, applyFilter, setLoading, getPlanFeatures]);

    const handleEdit = React.useCallback((planFeature) => {
        history.push(`${match.url}/${planFeature.id}/edit`);
    }, [history, match.url]);

    const handleDelete = React.useCallback(async (planFeature) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete the plan feature "${planFeature.titleEn || planFeature.code}"? This action cannot be undone.`
        );
        
        if (!confirmed) {
            return;
        }

        try {
            setLoading(true);
            // Delete via API
            const result = await apiHelper.hkiff.deletePlanFeatureById(planFeature.id);
            
            if (result && (result.status === 200 || result.status === 204 || result.data)) {
                // Remove the item from allData
                const updatedData = allData.filter(item => item.id !== planFeature.id);
                setAllData(updatedData);
                // Reapply current filter
                applyFilter(selectedTab, updatedData);
                alert("Plan feature deleted successfully!");
            } else {
                alert("Failed to delete plan feature. Please try again.");
            }
        } catch (error) {
            console.error("Error deleting plan feature:", error);
            alert("An error occurred while deleting the plan feature. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [allData, selectedTab, applyFilter, setLoading]);

    return (
        <div className={classes.content}>
            <Typography variant="h4" gutterBottom>
                {"Cineclub > Plan Feature List"}
            </Typography>
            <Box style={{ marginBottom: "20px", borderBottom: "1px solid #e0e0e0" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                    <Tabs
                        value={selectedTab}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab label="All" />
                        <Tab label="Homepage" />
                        <Tab label="Register" />
                        <Tab label="Renewal" />
                    </Tabs>
                    <Button
                        variant="contained"
                        color="primary"
                        component={Link}
                        to={`${match.url}/new`}
                        startIcon={<Add />}
                    >
                        Add Feature
                    </Button>
                </Box>
            </Box>
            <form>
                <Grid container xs={12}>
                    <TableContainer
                        component={Paper}
                        style={{
                            boxShadow: "none",
                            border: "1px solid #cccccc",
                        }}
                    >
                        <fieldset disabled={disabled} className="fieldset">
                            <Table
                                aria-label="plan feature table"
                                component="ul"
                                style={{margin: "0"}}
                            >
                                <colgroup>
                                    <col style={{width: "8%"}}/>
                                    <col style={{width: "15%"}}/>
                                    <col style={{width: "28%"}}/>
                                    <col style={{width: "28%"}}/>
                                    <col style={{width: "8%"}}/>
                                    <col style={{width: "5%"}}/>
                                </colgroup>
                                <TableHead component="div">
                                    <TableRow>
                                        <TableCell
                                            style={{
                                                paddingTop: 6,
                                                paddingBottom: 6,
                                                lineHeight: "1.1em",
                                                height: "48px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            ID
                                        </TableCell>
                                        <TableCell
                                            style={{
                                                paddingTop: 6,
                                                paddingBottom: 6,
                                                lineHeight: "1.1em",
                                                height: "48px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Code
                                        </TableCell>
                                        {selectedTab === 0 && (
                                            <TableCell
                                                style={{
                                                    paddingTop: 6,
                                                    paddingBottom: 6,
                                                    lineHeight: "1.1em",
                                                    height: "48px",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                Type
                                            </TableCell>
                                        )}
                                        <TableCell
                                            style={{
                                                paddingTop: 6,
                                                paddingBottom: 6,
                                                lineHeight: "1.1em",
                                                height: "48px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Title (EN)
                                        </TableCell>
                                        <TableCell
                                            style={{
                                                paddingTop: 6,
                                                paddingBottom: 6,
                                                lineHeight: "1.1em",
                                                height: "48px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Title (TC)
                                        </TableCell>
                                        <TableCell
                                            style={{
                                                paddingTop: 6,
                                                paddingBottom: 6,
                                                lineHeight: "1.1em",
                                                height: "48px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Action
                                        </TableCell>
                                        {selectedTab === 0 && (
                                            <TableCell
                                                style={{
                                                    paddingTop: 6,
                                                    paddingBottom: 6,
                                                    lineHeight: "1.1em",
                                                    height: "48px",
                                                    fontWeight: "bold",
                                                }}
                                            />
                                        )}
                                    </TableRow>
                                </TableHead>
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="plan-features">
                                        {(provided) => (
                                            <TableBody
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                            >
                                                {data.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={7} align="center" style={{ padding: "20px" }}>
                                                            No plan features found
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    data.map((row, index) => (
                                                        <Draggable
                                                            key={row.id}
                                                            draggableId={String(row.id)}
                                                            index={index}
                                                            isDragDisabled={selectedTab !== 0}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <TableRow
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    style={{
                                                                        ...provided.draggableProps.style,
                                                                        backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'inherit',
                                                                    }}
                                                                >
                                                                    <TableCell
                                                                        style={{
                                                                            paddingTop: 6,
                                                                            paddingBottom: 6,
                                                                            lineHeight: "1.1em",
                                                                            height: "48px",
                                                                        }}
                                                                    >
                                                                        <span className={classes.text}>
                                                                            {row.id || "-"}
                                                                        </span>
                                                                    </TableCell>
                                                                    <TableCell
                                                                        style={{
                                                                            paddingTop: 6,
                                                                            paddingBottom: 6,
                                                                            lineHeight: "1.1em",
                                                                            height: "48px",
                                                                        }}
                                                                    >
                                                                        <span className={classes.text}>
                                                                            {row.code || "-"}
                                                                        </span>
                                                                    </TableCell>
                                                                    {selectedTab === 0 && (
                                                                        <TableCell
                                                                            style={{
                                                                                paddingTop: 6,
                                                                                paddingBottom: 6,
                                                                                lineHeight: "1.1em",
                                                                                height: "48px",
                                                                            }}
                                                                        >
                                                                            {row.type || "-"}
                                                                        </TableCell>
                                                                    )}
                                                                    <TableCell
                                                                        style={{
                                                                            paddingTop: 6,
                                                                            paddingBottom: 6,
                                                                            lineHeight: "1.1em",
                                                                            height: "48px",
                                                                        }}
                                                                    >
                                                                        {row.titleEn || "-"}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        style={{
                                                                            paddingTop: 6,
                                                                            paddingBottom: 6,
                                                                            lineHeight: "1.1em",
                                                                            height: "48px",
                                                                        }}
                                                                    >
                                                                        {row.titleTc || "-"}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        style={{
                                                                            paddingTop: 6,
                                                                            paddingBottom: 6,
                                                                            lineHeight: "1.1em",
                                                                            height: "48px",
                                                                        }}
                                                                    >
                                                                        <div style={{display: "inline-flex", gap: "8px"}}>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleEdit(row)}
                                                                                title="Edit"
                                                                            >
                                                                                <Edit fontSize="small"/>
                                                                            </IconButton>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleDelete(row)}
                                                                                title="Delete"
                                                                                style={{ color: '#f44336' }}
                                                                            >
                                                                                <Delete fontSize="small"/>
                                                                            </IconButton>
                                                                        </div>
                                                                    </TableCell>
                                                                    {selectedTab === 0 && (
                                                                        <TableCell
                                                                            style={{
                                                                                paddingTop: 6,
                                                                                paddingBottom: 6,
                                                                                lineHeight: "1.1em",
                                                                                height: "48px",
                                                                                color: "#CCCCCC",
                                                                            }}
                                                                            {...provided.dragHandleProps}
                                                                        >
                                                                            <DragHandleIcon />
                                                                        </TableCell>
                                                                    )}
                                                                </TableRow>
                                                            )}
                                                        </Draggable>
                                                    ))
                                                )}
                                                {provided.placeholder}
                                            </TableBody>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </Table>
                        </fieldset>
                    </TableContainer>
                </Grid>
            </form>
        </div>
    );
};

export default PlanFeatureListingPage;
